import { Box, Card, ColorInput, Grid, NumberInput, Select, Text, TextInput } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import ISO6391 from 'iso-639-1';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import Spinner from '../Spinner';
import LocalizedTextsTable, { isLocalizedTextsValid } from './LocalizedTextsTable';
import LoyaltyWidgetPreview from './LoyaltyWidgetPreview';

const defaultSettings = {
  palette: {
    primaryColor: '#228be6',
    secondaryColor: '#40c057',
    backgroundColor: '#ffffff',
    textColor: '#66686b',
    buttonTextColor: '#ffffff',
    panelTextColor: '#66686b',
  },
  typography: {
    fontFamily: 'inherit',
    headingFontSize: 16,
    bodyFontSize: 14,
    buttonFontSize: 14,
    panelFontSize: 14,
  },
  borderRadius: 8,
  logoAlignment: 'left',
  texts: {
    balanceText: { default: 'You have {{points}} points', overrides: {} },
    redeemButtonText: { default: 'Purchase for {{cost}} points', overrides: {} },
    couponsHeadingText: { default: 'Coupons available for purchase:', overrides: {} },
  },
};

// Helper to merge server settings with defaults, preserving unknown server keys
const mergeWithDefaults = (loyaltySettings, domainLocale = 'en') => {
  if (!loyaltySettings) {
    return defaultSettings;
  }

  return {
    ...defaultSettings,
    ...loyaltySettings,
    palette: { ...defaultSettings.palette, ...loyaltySettings.palette },
    typography: { ...defaultSettings.typography, ...loyaltySettings.typography },
    texts: Object.fromEntries(
      Object.keys({ ...defaultSettings.texts, ...loyaltySettings.texts }).map((key) => {
        const defaultText = defaultSettings.texts[key] || { default: '', overrides: {} };
        const serverText = loyaltySettings.texts?.[key];

        // Old shape: a flat { [locale]: string } map. Discriminate on the values
        // rather than on a missing `default` key, so a new-shape object that
        // simply omits `default` isn't mistaken for a legacy record.
        const isLegacyShape =
          serverText &&
          typeof serverText === 'object' &&
          !('default' in serverText) &&
          !('overrides' in serverText) &&
          Object.values(serverText).every((text) => typeof text === 'string');

        if (isLegacyShape) {
          // Migrate from old { [locale]: string } shape to new { default, overrides } shape
          const locales = { ...serverText };
          const promotedLocale = locales[domainLocale] ? domainLocale : locales.en ? 'en' : Object.keys(locales)[0];
          const overrides = { ...locales };
          delete overrides[promotedLocale];

          return [key, { default: locales[promotedLocale] || defaultText.default, overrides }];
        }

        // New shape - use as-is
        return [
          key,
          {
            default: serverText?.default ?? defaultText.default,
            overrides: { ...defaultText.overrides, ...serverText?.overrides },
          },
        ];
      })
    ),
  };
};

// Union of override locale keys across all 3 text fields - used both to seed
// the initial preview locale once settings load, and to build the preview
// locale picker's options live on every render.
const getOverrideLocales = (texts) => {
  const localeSet = new Set();
  Object.values(texts || {}).forEach((field) => {
    Object.keys(field?.overrides || {}).forEach((locale) => localeSet.add(locale));
  });
  return Array.from(localeSet);
};

const LoyaltyCustomizeTab = () => {
  const { domain } = useSelector((state) => state.domain);
  const fetch = useFetch();

  // Helper to coerce NumberInput values to numbers
  const coerceToNumber = (value, fallback) => {
    if (typeof value === 'number') return value;
    const parsed = Number(value);
    return isNaN(parsed) || value === '' ? fallback : parsed;
  };

  const [loyaltySettings, setLoyaltySettings] = useState();
  const [initialSettings, setInitialSettings] = useState();
  const [previewBaseUrl, setPreviewBaseUrl] = useState();
  const [loadError, setLoadError] = useState();
  const [saveError, setSaveError] = useState();
  const [saving, setSaving] = useState(false);

  const domainLocale = domain.locale || 'en';

  // Locale used to build the live preview iframe. Seeded to the domain's
  // configured locale once settings load (only if it actually has an
  // override; otherwise 'default'), but can be changed freely afterwards.
  const [previewLocale, setPreviewLocale] = useState('default');

  // Dirty check - compare stringified versions
  const initialSettingsStringified = JSON.stringify(initialSettings);
  const isDirty = initialSettingsStringified !== JSON.stringify(loyaltySettings);

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/loyalty/settings`)
      .then(({ loyaltySettings, previewBaseUrl }) => {
        const mergedSettings = mergeWithDefaults(loyaltySettings, domainLocale);
        setLoyaltySettings(mergedSettings);
        setInitialSettings(mergedSettings);
        setPreviewBaseUrl(previewBaseUrl);

        const overrideLocales = getOverrideLocales(mergedSettings.texts);
        setPreviewLocale(overrideLocales.includes(domainLocale) ? domainLocale : 'default');
      })
      .catch((error) => {
        setLoadError(error);
      });
  }, [domain.accountId, domain.id, fetch, domainLocale]);

  // Clear previewLocale back to 'default' if it falls out of the available options
  useEffect(() => {
    if (!loyaltySettings) return;
    const overrideLocales = getOverrideLocales(loyaltySettings.texts);
    if (previewLocale !== 'default' && !overrideLocales.includes(previewLocale)) {
      setPreviewLocale('default');
    }
  }, [loyaltySettings, previewLocale]);

  const validateTexts = () => isLocalizedTextsValid(loyaltySettings.texts);

  const handleSave = async () => {
    if (!validateTexts()) {
      notifications.show({
        title: 'Validation Error',
        message:
          'Please fix the highlighted text fields before saving - each one must include its required placeholder.',
        color: 'red',
      });
      return;
    }

    setSaving(true);
    setSaveError(undefined);

    try {
      const response = await fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/loyalty/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loyaltySettings }),
      });

      // Merge the response with defaults to handle empty or partial responses
      const mergedResponse = response.loyaltySettings
        ? mergeWithDefaults(response.loyaltySettings, domainLocale)
        : loyaltySettings;
      setInitialSettings(mergedResponse);
      setLoyaltySettings(mergedResponse);
      if (response.previewBaseUrl !== undefined) {
        setPreviewBaseUrl(response.previewBaseUrl);
      }
      notifications.show({
        title: 'Success',
        message: 'Loyalty settings saved successfully',
        color: 'green',
      });
    } catch (error) {
      setSaveError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (path, value) => {
    setLoyaltySettings((prev) => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (!loyaltySettings) {
    return <Spinner />;
  }

  return (
    <>
      <Grid mt="md">
        <Grid.Col span={{ base: 12, md: 6 }}>
          {/* Layout Section */}
          <Card withBorder mb="md">
            <Text fw={600} size="lg" mb="md">
              Layout
            </Text>
            <Grid>
              <Grid.Col span={4}>
                <label>Logo Alignment</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <Select
                  value={loyaltySettings.logoAlignment}
                  onChange={(value) => updateSettings('logoAlignment', value)}
                  data={[
                    { value: 'left', label: 'Left' },
                    { value: 'center', label: 'Center' },
                    { value: 'right', label: 'Right' },
                  ]}
                  allowDeselect={false}
                />
              </Grid.Col>
            </Grid>
          </Card>

          {/* Colors Section */}
          <Card withBorder mb="md">
            <Text fw={600} size="lg" mb="md">
              Colors
            </Text>
            <Grid mb="sm">
              <Grid.Col span={4}>
                <label>Primary Color</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <ColorInput
                  format="hex"
                  value={loyaltySettings.palette.primaryColor}
                  onChange={(value) => updateSettings('palette.primaryColor', value)}
                />
              </Grid.Col>
            </Grid>
            <Grid mb="sm">
              <Grid.Col span={4}>
                <label>Secondary Color</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <ColorInput
                  format="hex"
                  value={loyaltySettings.palette.secondaryColor}
                  onChange={(value) => updateSettings('palette.secondaryColor', value)}
                />
              </Grid.Col>
            </Grid>
            <Grid mb="sm">
              <Grid.Col span={4}>
                <label>Background Color</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <ColorInput
                  format="hex"
                  value={loyaltySettings.palette.backgroundColor}
                  onChange={(value) => updateSettings('palette.backgroundColor', value)}
                />
              </Grid.Col>
            </Grid>
            <Grid mb="sm">
              <Grid.Col span={4}>
                <label>Text Color</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <ColorInput
                  format="hex"
                  value={loyaltySettings.palette.textColor}
                  onChange={(value) => updateSettings('palette.textColor', value)}
                />
              </Grid.Col>
            </Grid>
            <Grid mb="sm">
              <Grid.Col span={4}>
                <label>Button Text Color</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <ColorInput
                  format="hex"
                  value={loyaltySettings.palette.buttonTextColor}
                  onChange={(value) => updateSettings('palette.buttonTextColor', value)}
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={4}>
                <label>Panel Text Color</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <ColorInput
                  format="hex"
                  value={loyaltySettings.palette.panelTextColor}
                  onChange={(value) => updateSettings('palette.panelTextColor', value)}
                />
                <Text size="xs" c="dimmed" mt={4}>
                  Controls the points-balance panel&apos;s text color, separately from Button Text Color.
                </Text>
              </Grid.Col>
            </Grid>
          </Card>

          {/* Typography Section */}
          <Card withBorder mb="md">
            <Text fw={600} size="lg" mb="md">
              Typography
            </Text>
            <Grid mb="sm">
              <Grid.Col span={4}>
                <label>Font Family</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <TextInput
                  value={loyaltySettings.typography.fontFamily}
                  onChange={(e) => updateSettings('typography.fontFamily', e.target.value)}
                  placeholder="e.g. Arial, sans-serif"
                />
              </Grid.Col>
            </Grid>
            <Grid mb="sm">
              <Grid.Col span={4}>
                <label>Heading Font Size</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <NumberInput
                  value={loyaltySettings.typography.headingFontSize}
                  onChange={(value) =>
                    updateSettings(
                      'typography.headingFontSize',
                      coerceToNumber(value, defaultSettings.typography.headingFontSize)
                    )
                  }
                  min={10}
                  max={32}
                  suffix=" px"
                />
              </Grid.Col>
            </Grid>
            <Grid mb="sm">
              <Grid.Col span={4}>
                <label>Body Font Size</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <NumberInput
                  value={loyaltySettings.typography.bodyFontSize}
                  onChange={(value) =>
                    updateSettings(
                      'typography.bodyFontSize',
                      coerceToNumber(value, defaultSettings.typography.bodyFontSize)
                    )
                  }
                  min={10}
                  max={24}
                  suffix=" px"
                />
              </Grid.Col>
            </Grid>
            <Grid mb="sm">
              <Grid.Col span={4}>
                <label>Button Font Size</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <NumberInput
                  value={loyaltySettings.typography.buttonFontSize}
                  onChange={(value) =>
                    updateSettings(
                      'typography.buttonFontSize',
                      coerceToNumber(value, defaultSettings.typography.buttonFontSize)
                    )
                  }
                  min={10}
                  max={24}
                  suffix=" px"
                />
              </Grid.Col>
            </Grid>
            <Grid mb="sm">
              <Grid.Col span={4}>
                <label>Panel Font Size</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <NumberInput
                  value={loyaltySettings.typography.panelFontSize}
                  onChange={(value) =>
                    updateSettings(
                      'typography.panelFontSize',
                      coerceToNumber(value, defaultSettings.typography.panelFontSize)
                    )
                  }
                  min={10}
                  max={24}
                  suffix=" px"
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={4}>
                <label>Border Radius</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <NumberInput
                  value={loyaltySettings.borderRadius}
                  onChange={(value) =>
                    updateSettings('borderRadius', coerceToNumber(value, defaultSettings.borderRadius))
                  }
                  min={0}
                  max={32}
                  suffix=" px"
                />
              </Grid.Col>
            </Grid>
          </Card>

          {/* Text Section */}
          <Card withBorder mb="md">
            <Text fw={600} size="lg" mb="md">
              Text
            </Text>
            <LocalizedTextsTable
              texts={loyaltySettings.texts}
              onChange={(newTexts) => updateSettings('texts', { ...loyaltySettings.texts, ...newTexts })}
            />
          </Card>
        </Grid.Col>

        {/* Preview Column */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder style={{ position: 'sticky', top: 20 }}>
            <Text fw={600} size="lg" mb="md">
              Preview
            </Text>
            <Grid mb="sm">
              <Grid.Col span={4}>
                <label>Preview Locale</label>
              </Grid.Col>
              <Grid.Col span={8}>
                <Select
                  value={previewLocale}
                  onChange={setPreviewLocale}
                  data={[
                    { value: 'default', label: 'Default' },
                    ...getOverrideLocales(loyaltySettings.texts).map((locale) => ({
                      value: locale,
                      label: ISO6391.getName(locale) ? `${ISO6391.getName(locale)} (${locale})` : locale,
                    })),
                  ]}
                  allowDeselect={false}
                  searchable
                />
              </Grid.Col>
            </Grid>
            <LoyaltyWidgetPreview
              loyaltySettings={loyaltySettings}
              previewBaseUrl={previewBaseUrl}
              logoUrl={domain?.logoUrl}
              locale={previewLocale}
            />
          </Card>
        </Grid.Col>
      </Grid>

      {/* Save Button Footer */}
      <Box ta="right" mt="md">
        <LockableButton
          restrictionKey="loyaltyUpdate"
          disabled={!isDirty || saving}
          loading={saving}
          onClick={handleSave}
        >
          <IconDeviceFloppy size={16} /> {isDirty ? 'Save' : 'Saved'}
        </LockableButton>
        {saveError && <ErrorMessage message={saveError.message || 'Failed to save loyalty settings.'} />}
      </Box>
    </>
  );
};

export default LoyaltyCustomizeTab;
